import React, { useState, useEffect, useMemo } from 'react';
import API from '../../utils/api';
import { CLASSES, SECTIONS, SUBJECTS } from '../../constants/roles';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal'; // Import Modal

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Removed DEFAULT_PERIODS, we will calculate them dynamically

const subjectColors = {
  Mathematics: 'bg-green-100 text-green-700 border-green-200',
  Science: 'bg-purple-100 text-purple-700 border-purple-200',
  English: 'bg-blue-100 text-blue-700 border-blue-200',
  Hindi: 'bg-pink-100 text-pink-700 border-pink-200',
  'Social Science': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Computer: 'bg-teal-100 text-teal-700 border-teal-200',
  'Physical Education': 'bg-orange-100 text-orange-700 border-orange-200',
  Art: 'bg-red-100 text-red-700 border-red-200',
  Free: 'bg-gray-100 text-gray-400 border-gray-200',
};


export default function Timetable() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'admin2';
  
  const [cls, setCls] = useState(CLASSES.includes('8th') ? '8th' : CLASSES[0]);
  const [section, setSection] = useState('A');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [teachers, setTeachers] = useState([]);
  const [teacherSchedules, setTeacherSchedules] = useState({});
  const [timetableStatus, setTimetableStatus] = useState('draft');
  
  const [grid, setGrid] = useState({});
  const [showSubjectTeacher, setShowSubjectTeacher] = useState(true);
  const [draggedSubject, setDraggedSubject] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);

  // New states for dynamic settings
  const [periodDuration, setPeriodDuration] = useState(45);
  const [breakDuration, setBreakDuration] = useState(15);
  
  // Assigned subjects for the right panel
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  
  // Add Subject Modal
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [addSubjectForm, setAddSubjectForm] = useState({ subject: '', teacher_id: '' });

  useEffect(() => {
    Promise.all([
      API.get('/teachers?status=active'),
      API.get('/timetable/teacher-schedules')
    ]).then(([teachersRes, schedulesRes]) => {
      setTeachers(teachersRes.data || []);
      setTeacherSchedules(schedulesRes.data || {});
    }).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    loadTimetable();
    loadAssignedSubjects();
  }, [cls, section]);

  const loadAssignedSubjects = async () => {
    try {
      const res = await API.get(`/class-subjects?class=${cls}&section=${section}`);
      setAssignedSubjects(res.data || []);
    } catch (err) {
      toast.error('Failed to load assigned subjects');
    }
  };

  const loadTimetable = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/timetable?class=${cls}&section=${section}`);
      const data = res.data || [];
      const newGrid = {};
      DAYS.forEach(d => { newGrid[d] = {}; });
      
      let status = 'draft';
      data.forEach(slot => {
        if (slot.day && newGrid[slot.day]) {
          newGrid[slot.day][slot.period_number] = {
            subject: slot.subject,
            teacher_id: slot.teacher_id,
            teacher_name: slot.teacher?.name || ''
          };
          if (slot.status === 'published') status = 'published';
        }
      });
      setGrid(newGrid);
      setTimetableStatus(status);
    } catch (err) {
      toast.error('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const hasConflict = (teacherId, day, periodNum) => {
    if (!teacherId) return false;
    const schedule = teacherSchedules[teacherId];
    if (!schedule) return false;
    const periodsOnDay = schedule[day] || [];
    return periodsOnDay.includes(periodNum);
  };

  const handleDragStart = (e, subject, teacher) => {
    setDraggedSubject({ subject, teacher });
    e.dataTransfer.setData('text/plain', subject);
  };

  const handleDrop = (e, day, periodNum) => {
    e.preventDefault();
    if (!draggedSubject) return;

    if (hasConflict(draggedSubject.teacher?.id, day, periodNum)) {
      return toast.error(`${draggedSubject.teacher.name} is busy on ${day} P${periodNum}.`);
    }

    setGrid(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [periodNum]: {
          subject: draggedSubject.subject,
          teacher_id: draggedSubject.teacher?.id,
          teacher_name: draggedSubject.teacher?.name
        }
      }
    }));
    setTimetableStatus('draft');
  };

  const handleCellClick = (day, periodNum) => {
    setGrid(prev => {
      const newDay = { ...prev[day] };
      delete newDay[periodNum];
      return { ...prev, [day]: newDay };
    });
    setTimetableStatus('draft');
  };

  const assignSubjectToCell = (assignment) => {
    if (!selectedCell) return;
    setGrid(prev => ({
      ...prev,
      [selectedCell.day]: {
        ...prev[selectedCell.day],
        [selectedCell.num]: {
          subject: assignment.subject,
          teacher_id: assignment.teacher?.id,
          teacher_name: assignment.teacher?.name
        }
      }
    }));
    setTimetableStatus('draft');
    setSelectedCell(null);
  };

  const handleAddSubjectSubmit = async (e) => {
    e.preventDefault();
    if (!addSubjectForm.subject || !addSubjectForm.teacher_id) {
      return toast.error('Please select both a subject and a teacher.');
    }
    setSaving(true);
    try {
      await API.post('/class-subjects', {
        class: cls,
        section,
        subject: addSubjectForm.subject,
        teacher_id: parseInt(addSubjectForm.teacher_id, 10)
      });
      toast.success('Subject assigned successfully!');
      setShowAddSubject(false);
      setAddSubjectForm({ subject: '', teacher_id: '' });
      loadAssignedSubjects();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign subject');
    } finally {
      setSaving(false);
    }
  };

  const removeAssignedSubject = async (id) => {
    if (!window.confirm('Remove this subject assignment?')) return;
    try {
      await API.delete(`/class-subjects/${id}`);
      toast.success('Subject removed');
      loadAssignedSubjects();
    } catch (err) {
      toast.error('Failed to remove subject');
    }
  };

  const autoGenerate = () => {
    if (assignedSubjects.length === 0) return toast.error('No subjects assigned. Please assign subjects first.');

    const newGrid = {};
    DAYS.forEach(d => { newGrid[d] = {}; });
    
    let subIndex = 0;
    DAYS.forEach(day => {
      periods.filter(p => !p.isBreak).forEach(p => {
        let assigned = false;
        let attempts = 0;
        while (!assigned && attempts < assignedSubjects.length) {
          const assignment = assignedSubjects[subIndex % assignedSubjects.length];
          if (!hasConflict(assignment.teacher_id, day, p.num)) {
            newGrid[day][p.num] = {
              subject: assignment.subject,
              teacher_id: assignment.teacher_id,
              teacher_name: assignment.teacher?.name
            };
            assigned = true;
          }
          subIndex++;
          attempts++;
        }
      });
    });

    setGrid(newGrid);
    setTimetableStatus('draft');
    toast.success('Auto-generated (Draft)');
  };

  // Dynamic Period Calculation
  const periods = useMemo(() => {
    const list = [];
    let currentMins = 8 * 60; // 08:00 AM in minutes
    
    const formatTime = (mins) => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hr = h > 12 ? h - 12 : (h === 0 ? 12 : h);
      return `${hr.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`;
    };

    for (let i = 1; i <= 7; i++) {
      // After 3rd period, add break
      if (i === 4) {
        const breakStart = currentMins;
        currentMins += parseInt(breakDuration);
        list.push({ isBreak: true, time: `${formatTime(breakStart)} - ${formatTime(currentMins)}` });
      }
      
      const pStart = currentMins;
      currentMins += parseInt(periodDuration);
      list.push({ num: i, time: `${formatTime(pStart)} - ${formatTime(currentMins)}` });
    }
    return list;
  }, [periodDuration, breakDuration]);

  const saveTimetable = async (publish = false) => {
    const slots = [];
    DAYS.forEach(day => {
      if (!grid[day]) return;
      Object.entries(grid[day]).forEach(([periodNum, data]) => {
        if (data && data.subject) {
          slots.push({
            day,
            period_number: parseInt(periodNum),
            subject: data.subject,
            teacher_id: data.teacher_id
          });
        }
      });
    });

    setSaving(true);
    try {
      const finalStatus = publish ? 'published' : 'draft';
      await API.put('/timetable/bulk-save', {
        class: cls,
        section,
        slots,
        status: finalStatus
      });
      toast.success(publish ? 'Published!' : 'Draft Saved!');
      setTimetableStatus(finalStatus);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const clearTimetable = () => {
    if (!window.confirm('Reset timetable?')) return;
    const newGrid = {};
    DAYS.forEach(d => { newGrid[d] = {}; });
    setGrid(newGrid);
    setTimetableStatus('draft');
  };

  const f = 'border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold outline-none focus:border-blue-500 bg-white shadow-sm';

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Class</label>
            <select value={cls} onChange={e => setCls(e.target.value)} className={f}>
              {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Section</label>
            <select value={section} onChange={e => setSection(e.target.value)} className={f}>
              {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Academic Term</label>
            <select className={f}>
              <option>2024 - 2025 (Term 1)</option>
              <option>2024 - 2025 (Term 2)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Effective From</label>
            <input type="date" className={f} defaultValue="2024-05-27" />
          </div>
          
          <div className="flex items-center gap-2 mt-5">
            {timetableStatus === 'published' ? (
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full border border-green-200">● Published</span>
            ) : (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full border border-yellow-200">● Draft Mode</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-4 lg:mt-0 items-end">
          <button onClick={autoGenerate} className="px-4 py-2.5 rounded-xl border border-blue-200 text-blue-600 font-bold text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 bg-white">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
            Auto Generate
          </button>
          
          <button 
            onClick={() => saveTimetable(false)} 
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-[#6E1A33] text-white font-bold text-sm shadow-md hover:bg-[#5a1529] disabled:opacity-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
            {saving ? 'Saving...' : 'Save Timetable'}
          </button>
          
          <button 
            onClick={() => saveTimetable(false)} 
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-gray-800 text-white font-bold text-sm shadow-md hover:bg-gray-900 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>

          {isAdmin && (
            <button 
              onClick={() => { if(window.confirm('Publish to students?')) saveTimetable(true); }}
              className="px-5 py-2.5 rounded-xl bg-green-600 text-white font-bold text-sm shadow-md shadow-green-600/20 hover:bg-green-700"
            >
              Publish Timetable
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6">
        <div className="flex-1 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 h-fit">
          <div className="overflow-x-auto">
            <table className="w-full text-center border-collapse">
              <thead>
                <tr>
                  <th className="p-3 border-b border-r border-gray-100 w-24 align-top">
                    <div className="flex justify-between items-center text-[10px] font-bold text-gray-800">
                      <span>Periods →</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-bold text-gray-800 mt-2">
                      <span>Days ↓</span>
                    </div>
                  </th>
                  {periods.map((p, i) => (
                    <th key={i} className="p-3 border-b border-gray-100 min-w-[100px]">
                      {p.isBreak ? (
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-bold text-gray-800">Break</span>
                          <svg className="w-4 h-4 text-gray-400 mt-1 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">{p.time.split(' - ')[0]}<br/>{p.time.split(' - ')[1]}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <span className="text-sm font-extrabold text-gray-800">{p.num}</span>
                          <span className="text-[10px] text-gray-500 font-medium mt-1">{p.time.split(' - ')[0]}</span>
                          <span className="text-[10px] text-gray-500 font-medium">{p.time.split(' - ')[1]}</span>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => (
                  <tr key={day} className="group h-[80px]">
                    <td className="p-3 border-r border-b border-gray-100 font-bold text-gray-800 text-xs bg-white text-left align-middle">
                      {day}
                    </td>
                    {periods.map((p, i) => {
                      if (p.isBreak) {
                        return (
                          <td key={i} className="border-b border-gray-100 bg-gray-50/30 p-2 align-middle">
                            <div className="h-full w-full flex items-center justify-center text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">
                              BREAK
                            </div>
                          </td>
                        );
                      }

                      const slot = grid[day]?.[p.num];
                      const subj = slot?.subject;
                      const teacherName = slot?.teacher_name;
                      const colorClass = subjectColors[subj] || 'bg-gray-50 text-gray-400 border-dashed border-gray-200';

                      return (
                        <td
                          key={i}
                          onDragOver={e => e.preventDefault()}
                          onDrop={(e) => handleDrop(e, day, p.num)}
                          onClick={() => {
                            if (subj) {
                              handleCellClick(day, p.num);
                            } else {
                              setSelectedCell({ day, num: p.num });
                            }
                          }}
                          className={`border-b border-r border-gray-100 p-2 transition-all cursor-pointer hover:bg-gray-50/50 ${!subj && 'hover:bg-blue-50/30'}`}
                        >
                          <div className={`h-full min-h-[60px] w-full flex flex-col items-center justify-center rounded-lg border p-1 transition-all ${subj ? colorClass + ' shadow-sm' : 'bg-transparent border-dashed border-gray-200'}`}>
                            {subj ? (
                              <>
                                <span className="font-bold text-[11px] text-center leading-tight">{subj}</span>
                                {showSubjectTeacher && teacherName && (
                                  <span className="text-[10px] font-medium opacity-80 mt-1 text-center truncate w-full px-1">
                                    {teacherName}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-200 font-bold text-lg group-hover:text-blue-300">+</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 bg-blue-50/50 rounded-xl mt-4 flex items-center gap-3">
            <div className="p-1.5 bg-white rounded-full text-blue-600 shadow-sm">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
            </div>
            <p className="text-xs text-blue-800 font-medium">Tip: Drag and drop subjects or click on a cell to assign a subject.</p>
          </div>
        </div>

        <div className="xl:w-[320px] space-y-6">
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Assigned Subjects</h3>
              <button 
                onClick={() => setShowAddSubject(true)}
                className="text-blue-600 text-xs font-semibold flex items-center gap-1 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Subject
              </button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {assignedSubjects.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No subjects assigned yet.</p>
              ) : (
                assignedSubjects.map(assignment => (
                  <div
                    key={assignment.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, assignment.subject, assignment.teacher)}
                    className="flex items-center justify-between p-3 py-2.5 rounded-xl cursor-move transition-transform hover:-translate-y-0.5 hover:bg-gray-50 border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${subjectColors[assignment.subject] ? subjectColors[assignment.subject].split(' ')[0] : 'bg-gray-300'}`}></div>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-gray-700">{assignment.subject}</span>
                        <span className="text-[10px] text-gray-400 font-medium">
                          {assignment.teacher?.name ? assignment.teacher.name.split(' ')[0] : 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => removeAssignedSubject(assignment.id)} className="text-gray-300 hover:text-red-500 transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Settings
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-700">Period Duration</span>
                <select 
                  value={periodDuration} 
                  onChange={(e) => setPeriodDuration(e.target.value)} 
                  className="text-xs border-gray-200 rounded-lg py-1 px-2 bg-white outline-none font-medium text-gray-600"
                >
                  <option value={30}>30 Minutes</option>
                  <option value={45}>45 Minutes</option>
                  <option value={60}>60 Minutes</option>
                </select>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-700">Break Duration</span>
                <select 
                  value={breakDuration} 
                  onChange={(e) => setBreakDuration(e.target.value)} 
                  className="text-xs border-gray-200 rounded-lg py-1 px-2 bg-white outline-none font-medium text-gray-600"
                >
                  <option value={15}>15 Minutes</option>
                  <option value={20}>20 Minutes</option>
                  <option value={30}>30 Minutes</option>
                </select>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs font-semibold text-gray-700">Show Subject Teacher</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={showSubjectTeacher} onChange={(e) => setShowSubjectTeacher(e.target.checked)} />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#6E1A33]"></div>
                </label>
              </div>
            </div>
            <button onClick={clearTimetable} className="w-full mt-6 py-2.5 rounded-xl border border-red-200 text-red-500 font-bold text-xs hover:bg-red-50 flex items-center justify-center gap-2 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Reset Timetable
            </button>
          </div>
        </div>
      </div>
      <Modal isOpen={showAddSubject} onClose={() => setShowAddSubject(false)} title="Assign Teacher to Subject">
        <form onSubmit={handleAddSubjectSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Select Subject</label>
            <select
              value={addSubjectForm.subject}
              onChange={e => setAddSubjectForm(p => ({ ...p, subject: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-blue-500 bg-white"
              required
            >
              <option value="">Choose a subject...</option>
              {SUBJECTS.map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Assign Teacher</label>
            <select
              value={addSubjectForm.teacher_id}
              onChange={e => setAddSubjectForm(p => ({ ...p, teacher_id: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-blue-500 bg-white"
              required
            >
              <option value="">Choose a teacher...</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.name} (Specialty: {t.subject})</option>
              ))}
            </select>
          </div>
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-500 active:bg-blue-700 px-5 py-2.5 text-sm font-extrabold text-white rounded-xl shadow-lg shadow-blue-600/15 transition-all disabled:opacity-60"
            >
              {saving ? 'Assigning...' : 'Assign Teacher'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Select Subject Modal (For Mobile/Click) */}
      {selectedCell && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden transform transition-all border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div>
                <h3 className="font-bold text-gray-800">Select Subject</h3>
                <p className="text-xs text-gray-500 font-medium mt-0.5">{selectedCell.day} - Period {selectedCell.num}</p>
              </div>
              <button onClick={() => setSelectedCell(null)} className="text-gray-400 hover:text-gray-700 bg-white shadow-sm p-1.5 rounded-lg border border-gray-100">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {assignedSubjects.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500 font-medium">No subjects assigned yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Please assign subjects to this class first.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {assignedSubjects.map(assignment => (
                    <button
                      key={assignment.id}
                      onClick={() => assignSubjectToCell(assignment)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${subjectColors[assignment.subject] ? subjectColors[assignment.subject].split(' ')[0] : 'bg-gray-300'}`}></div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-700">{assignment.subject}</span>
                          <span className="text-xs text-gray-500 font-medium mt-0.5">
                            {assignment.teacher?.name || 'Unknown Teacher'}
                          </span>
                        </div>
                      </div>
                      <div className="text-blue-500 bg-white p-1 rounded-full shadow-sm">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}